import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, addDays, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  LayoutDashboard, CalendarDays, Clock, Users, Wallet, UserCircle,
  Video, CheckCircle, XCircle, Star, Trash2, Save, Plane, AlertCircle,
  TrendingUp, Shield, Bell, ChevronLeft, ChevronRight, CalendarCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import api, { mensajeDeError, pesos } from '../lib/api';
import { Armazon } from '../components/panel/Armazon';
import {
  Tarjeta, TarjetaTitulo, TarjetaCuerpo, Metrica, Estado, Vacio, Esqueleto,
} from '../components/panel/Piezas';
import { PrimerosPasos } from '../components/PrimerosPasos';
import { toast } from 'sonner';

/**
 * Panel del profesional.
 *
 * Deliberadamente NO tiene ficha clínica ni notas de sesión: guardar información
 * clínica del paciente convertiría la plataforma en un sistema de ficha clínica,
 * con las obligaciones legales que eso implica. Aquí se gestiona la agenda, las
 * horas y los ingresos.
 *
 * Del paciente solo se ve el seudónimo. Nunca correo ni forma de contacto.
 */

const DIAS = [
  { n: 1, corto: 'Lun' }, { n: 2, corto: 'Mar' }, { n: 3, corto: 'Mié' },
  { n: 4, corto: 'Jue' }, { n: 5, corto: 'Vie' }, { n: 6, corto: 'Sáb' }, { n: 7, corto: 'Dom' },
];

const HORAS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00',
];

const ESTADOS = {
  pending: { texto: 'Pendiente', tono: 'alerta' },
  confirmed: { texto: 'Confirmada', tono: 'exito' },
  completed: { texto: 'Realizada', tono: 'neutro' },
  cancelled: { texto: 'Cancelada', tono: 'riesgo' },
  no_show: { texto: 'No asistió', tono: 'riesgo' },
};

const PsychologistDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [cargando, setCargando] = useState(true);
  const [seccion, setSeccion] = useState('resumen');

  const [hoy, setHoy] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [stats, setStats] = useState(null);
  const [reservas, setReservas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [valoraciones, setValoraciones] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [primerosPasos, setPrimerosPasos] = useState(null);

  const [horario, setHorario] = useState({});
  const [excepciones, setExcepciones] = useState([]);
  const [guardandoHorario, setGuardandoHorario] = useState(false);
  const [pintando, setPintando] = useState(null);

  const [semana, setSemana] = useState(null);
  const [semanaDesde, setSemanaDesde] = useState(
    format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  );

  const [ingresos, setIngresos] = useState(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [anio, setAnio] = useState(new Date().getFullYear());

  const [bloqueoDesde, setBloqueoDesde] = useState('');
  const [bloqueoHasta, setBloqueoHasta] = useState('');
  const [bloqueoNota, setBloqueoNota] = useState('');

  const salir = useCallback(() => {
    logout();
    navigate('/profesional/login');
  }, [logout, navigate]);

  const manejarError = useCallback(
    (error, porDefecto) => {
      const estado = error?.response?.status;
      if (estado === 401 || estado === 403) {
        salir();
        return;
      }
      toast.error(mensajeDeError(error, porDefecto));
    },
    [salir]
  );

  /**
   * Carga el panel con allSettled y no con Promise.all: si una sola llamada
   * falla, Promise.all descartaría TODAS las respuestas y el panel quedaría en
   * blanco, como si el profesional no tuviera pacientes.
   */
  const cargarTodo = useCallback(async () => {
    const partes = [
      ['resumen', '/dashboard/hoy', setHoy],
      ['perfil', '/dashboard/profile', setPerfil],
      ['estadísticas', '/dashboard/stats', setStats],
      ['reservas', '/dashboard/reservas', setReservas],
      ['pacientes', '/dashboard/pacientes', setPacientes],
      ['horario', '/dashboard/horario', (d) => setHorario(d.weekdays || {})],
      ['avisos', '/dashboard/notificaciones', setAvisos],
      ['valoraciones', '/dashboard/valoraciones', setValoraciones],
      ['primeros pasos', '/dashboard/primeros-pasos', setPrimerosPasos],
    ];

    const resultados = await Promise.allSettled(partes.map(([, url]) => api.get(url)));

    let sesionCaida = false;
    const fallaron = [];

    resultados.forEach((resultado, i) => {
      const [nombre, , asignar] = partes[i];
      if (resultado.status === 'fulfilled') {
        asignar(resultado.value.data);
        return;
      }
      const estado = resultado.reason?.response?.status;
      if (estado === 401 || estado === 403) sesionCaida = true;
      else fallaron.push(nombre);
    });

    setCargando(false);

    if (sesionCaida) {
      salir();
      return;
    }
    if (fallaron.length > 0) {
      toast.error(`No pudimos cargar: ${fallaron.join(', ')}. El resto funciona.`);
    }
  }, [salir]);

  useEffect(() => {
    const token = sessionStorage.getItem('anonimamente_token');
    const usuario = sessionStorage.getItem('anonimamente_user');

    if (!token || !usuario) {
      navigate('/profesional/login');
      return;
    }
    try {
      if (JSON.parse(usuario).type !== 'psychologist') {
        navigate('/profesional/login');
        return;
      }
    } catch {
      navigate('/profesional/login');
      return;
    }
    cargarTodo();
  }, [navigate, cargarTodo]);

  const cargarExcepciones = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/excepciones');
      setExcepciones(data);
    } catch (error) {
      manejarError(error, 'No pudimos cargar tus bloqueos');
    }
  }, [manejarError]);

  const cargarIngresos = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/ingresos', { params: { year: anio, month: mes } });
      setIngresos(data);
    } catch (error) {
      manejarError(error, 'No pudimos cargar tus ingresos');
    }
  }, [anio, mes, manejarError]);

  const cargarSemana = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/semana', { params: { from: semanaDesde } });
      setSemana(data);
    } catch (error) {
      manejarError(error, 'No pudimos cargar tu semana');
    }
  }, [semanaDesde, manejarError]);

  useEffect(() => {
    if (seccion === 'horario') cargarExcepciones();
    if (seccion === 'ingresos') cargarIngresos();
    if (seccion === 'agenda') cargarSemana();
  }, [seccion, cargarExcepciones, cargarIngresos, cargarSemana]);

  // ---------- Acciones ----------

  const alternarHora = (dia, hora) => {
    setHorario((actual) => {
      const delDia = actual[dia] || [];
      const nuevo = delDia.includes(hora)
        ? delDia.filter((h) => h !== hora)
        : [...delDia, hora].sort();
      return { ...actual, [dia]: nuevo };
    });
  };

  /** Arrastrar sobre la grilla marca varias horas de una pasada. */
  const pintarSobre = (dia, hora) => {
    if (pintando === null) return;
    const activo = (horario[dia] || []).includes(hora);
    if (activo !== pintando) alternarHora(dia, hora);
  };

  const copiarDia = (origen) => {
    const horas = horario[origen] || [];
    const nuevo = { ...horario };
    [1, 2, 3, 4, 5].forEach((d) => {
      nuevo[d] = [...horas];
    });
    setHorario(nuevo);
    toast.success('Copiado a la semana laboral');
  };

  const guardarHorario = async () => {
    setGuardandoHorario(true);
    try {
      const { data } = await api.put('/dashboard/horario', { weekdays: horario });
      toast.success(`${data.slots} horarios guardados`);
      cargarTodo();
    } catch (error) {
      manejarError(error, 'No pudimos guardar tu horario');
    } finally {
      setGuardandoHorario(false);
    }
  };

  const bloquearDias = async () => {
    if (!bloqueoDesde) {
      toast.error('Elige al menos la fecha de inicio');
      return;
    }

    try {
      const { data } = await api.post('/dashboard/excepciones', {
        date: bloqueoDesde,
        date_to: bloqueoHasta || bloqueoDesde,
        kind: 'block',
        note: bloqueoNota,
      });
      toast.success(data.message);
      if (data.warning) toast.warning(data.warning, { duration: 9000 });
      setBloqueoDesde('');
      setBloqueoHasta('');
      setBloqueoNota('');
      cargarExcepciones();
    } catch (error) {
      manejarError(error, 'No pudimos bloquear esas fechas');
    }
  };

  const quitarExcepcion = async (id) => {
    try {
      await api.delete(`/dashboard/excepciones/${id}`);
      cargarExcepciones();
    } catch (error) {
      manejarError(error, 'No pudimos eliminar el bloqueo');
    }
  };

  const marcarAsistencia = async (id, estado) => {
    try {
      await api.patch(`/dashboard/reservas/${id}/asistencia`, { status: estado });
      toast.success(estado === 'completed' ? 'Sesión marcada como realizada' : 'Ausencia registrada');
      cargarTodo();
    } catch (error) {
      manejarError(error, 'No pudimos registrar la asistencia');
    }
  };

  const guardarPerfil = async (cambios) => {
    try {
      await api.patch('/dashboard/profile', cambios);
      toast.success('Perfil actualizado');
      cargarTodo();
    } catch (error) {
      manejarError(error, 'No pudimos actualizar tu perfil');
    }
  };

  const sinLeer = avisos.filter((a) => !a.read).length;
  const porMarcar = hoy?.pending_attendance?.length ?? 0;

  const secciones = [
    { id: 'resumen', nombre: 'Resumen', icono: LayoutDashboard, insignia: porMarcar },
    { id: 'agenda', nombre: 'Mi semana', icono: CalendarDays },
    { id: 'horario', nombre: 'Mi horario', icono: Clock },
    { id: 'reservas', nombre: 'Reservas', icono: CalendarCheck },
    { id: 'pacientes', nombre: 'Pacientes', icono: Users },
    { id: 'ingresos', nombre: 'Ingresos', icono: Wallet },
    { id: 'perfil', nombre: 'Mi perfil', icono: UserCircle },
  ];

  if (cargando) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-8">
        <div className="max-w-3xl mx-auto">
          <Esqueleto filas={5} />
        </div>
      </div>
    );
  }

  return (
    <Armazon
      titulo={perfil?.name || 'Mi panel'}
      subtitulo="Panel profesional"
      secciones={secciones}
      activa={seccion}
      onCambiar={setSeccion}
      onSalir={salir}
      acciones={
        sinLeer > 0 ? (
          <button
            type="button"
            onClick={async () => {
              await api.post('/dashboard/notificaciones/leidas').catch(() => {});
              cargarTodo();
            }}
            className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-teal-600"
            aria-label={`${sinLeer} avisos sin leer`}
          >
            <Bell className="w-5 h-5" />
            <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-teal-500" />
          </button>
        ) : null
      }
    >
      {/* ---------------- RESUMEN ---------------- */}
      {seccion === 'resumen' && (
        <div className="space-y-6">
          <PrimerosPasos datos={primerosPasos} onIr={setSeccion} />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metrica etiqueta="Sesiones hoy" valor={hoy?.today?.length ?? 0} icono={CalendarDays} />
            <Metrica etiqueta="Esta semana" valor={hoy?.week_sessions ?? 0} icono={TrendingUp} />
            <Metrica
              etiqueta="Por cobrar"
              valor={hoy?.pending_payment ?? 0}
              icono={AlertCircle}
              tono={(hoy?.pending_payment ?? 0) > 0 ? 'alerta' : 'neutro'}
            />
            <Metrica
              etiqueta="Valoración"
              valor={stats?.rating_count > 0 ? `${stats.rating} ★` : '—'}
              pie={stats?.rating_count > 0 ? `${stats.rating_count} valoraciones` : 'aún sin valoraciones'}
              icono={Star}
            />
          </div>

          {hoy?.next_booking && (
            <Tarjeta className="border-teal-200 dark:border-teal-500/30 bg-teal-50/60 dark:bg-teal-500/5">
              <div className="p-5 flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-teal-700 dark:text-teal-300 mb-1">
                    Tu próxima sesión
                  </p>
                  <p className="font-heading text-xl font-semibold truncate">
                    {hoy.next_booking.patient_pseudonym}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 first-letter:uppercase">
                    {format(parseISO(hoy.next_booking.scheduled_date), "EEEE d 'de' MMMM, HH:mm", { locale: es })} hrs
                  </p>
                </div>
                <a href={hoy.next_booking.session_url} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                    <Video className="w-4 h-4 mr-2" />
                    Ir a la sala
                  </Button>
                </a>
              </div>
            </Tarjeta>
          )}

          {porMarcar > 0 && (
            <Tarjeta>
              <TarjetaTitulo
                titulo="¿Cómo te fue en estas sesiones?"
                detalle="Márcalas para que tus estadísticas y tu liquidación cuadren."
              />
              <TarjetaCuerpo className="space-y-2">
                {hoy.pending_attendance.map((r) => (
                  <FilaReserva key={r.id} reserva={r} onAsistencia={marcarAsistencia} />
                ))}
              </TarjetaCuerpo>
            </Tarjeta>
          )}

          <Tarjeta>
            <TarjetaTitulo
              titulo="Agenda de hoy"
              detalle={hoy?.date && format(parseISO(`${hoy.date}T00:00:00`), "EEEE d 'de' MMMM", { locale: es })}
            />
            <TarjetaCuerpo>
              {(hoy?.today?.length ?? 0) === 0 ? (
                <Vacio icono={CalendarDays} titulo="Hoy no tienes sesiones" />
              ) : (
                <div className="space-y-2">
                  {hoy.today.map((r) => (
                    <FilaReserva key={r.id} reserva={r} onAsistencia={marcarAsistencia} mostrarSala />
                  ))}
                </div>
              )}
            </TarjetaCuerpo>
          </Tarjeta>

          <Tarjeta>
            <TarjetaTitulo titulo="Lo que viene" detalle="Tus próximas sesiones agendadas." />
            <TarjetaCuerpo>
              {(hoy?.upcoming?.length ?? 0) === 0 ? (
                <Vacio
                  icono={CalendarDays}
                  titulo="Sin sesiones agendadas"
                  texto="Cuando alguien reserve contigo, aparecerá acá."
                />
              ) : (
                <div className="space-y-2">
                  {hoy.upcoming.map((r) => (
                    <FilaReserva key={r.id} reserva={r} onAsistencia={marcarAsistencia} mostrarSala />
                  ))}
                </div>
              )}
            </TarjetaCuerpo>
          </Tarjeta>
        </div>
      )}

      {/* ---------------- MI SEMANA ---------------- */}
      {seccion === 'agenda' && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSemanaDesde(format(addDays(parseISO(semanaDesde), -7), 'yyyy-MM-dd'))}
                aria-label="Semana anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setSemanaDesde(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'))
                }
              >
                Esta semana
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSemanaDesde(format(addDays(parseISO(semanaDesde), 7), 'yyyy-MM-dd'))}
                aria-label="Semana siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {semana && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {format(parseISO(`${semana.from}T00:00:00`), "d 'de' MMM", { locale: es })} —{' '}
                {format(parseISO(`${semana.to}T00:00:00`), "d 'de' MMM", { locale: es })}
              </p>
            )}
          </div>

          {!semana ? (
            <Esqueleto filas={3} />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
              {semana.days.map((dia) => {
                const fecha = parseISO(`${dia.date}T00:00:00`);
                const esHoy = dia.date === format(new Date(), 'yyyy-MM-dd');

                return (
                  <Tarjeta key={dia.date} className={esHoy ? 'border-teal-400 dark:border-teal-500/50' : ''}>
                    <div className="p-3">
                      <p className={`text-sm font-medium first-letter:uppercase ${esHoy ? 'text-teal-700 dark:text-teal-300' : ''}`}>
                        {format(fecha, 'EEEE', { locale: es })}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                        {format(fecha, "d 'de' MMM", { locale: es })}
                        {dia.booked > 0 && ` · ${dia.booked} tomada${dia.booked > 1 ? 's' : ''}`}
                      </p>

                      {dia.slots.length === 0 ? (
                        <p className="text-xs text-slate-400 dark:text-slate-600 py-2">No atiendes</p>
                      ) : (
                        <div className="space-y-1">
                          {dia.slots.map((s) => (
                            <div
                              key={s.time}
                              title={s.booking ? s.booking.patient : 'Libre'}
                              className={`text-xs px-2 py-1.5 rounded-md ${
                                s.booking
                                  ? 'bg-teal-600 text-white'
                                  : s.locked
                                    ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                              }`}
                            >
                              <span className="font-mono">{s.time}</span>
                              {s.booking && (
                                <span className="block truncate opacity-90">
                                  {s.booking.patient}
                                  {!s.booking.paid && ' · sin pagar'}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Tarjeta>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ---------------- MI HORARIO ---------------- */}
      {seccion === 'horario' && (
        <div className="space-y-6">
          <Tarjeta>
            <TarjetaTitulo
              titulo="Tu horario semanal"
              detalle="Marca las horas en que atiendes. Se repite todas las semanas; puedes arrastrar para marcar varias."
              accion={
                <Button
                  onClick={guardarHorario}
                  disabled={guardandoHorario}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {guardandoHorario ? 'Guardando…' : 'Guardar'}
                </Button>
              }
            />
            <TarjetaCuerpo>
              <div
                className="overflow-x-auto -mx-1 px-1 select-none"
                onMouseLeave={() => setPintando(null)}
                onMouseUp={() => setPintando(null)}
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left font-medium text-slate-500 dark:text-slate-400 pb-3 pr-3 text-xs">
                        Hora
                      </th>
                      {DIAS.map((d) => (
                        <th
                          key={d.n}
                          className="pb-3 px-1 font-medium text-xs text-slate-500 dark:text-slate-400 text-center min-w-[48px]"
                        >
                          {d.corto}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {HORAS.map((hora) => (
                      <tr key={hora}>
                        <td className="py-0.5 pr-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                          {hora}
                        </td>
                        {DIAS.map((d) => {
                          const activo = (horario[d.n] || []).includes(hora);
                          return (
                            <td key={d.n} className="py-0.5 px-1">
                              <button
                                type="button"
                                onMouseDown={() => {
                                  setPintando(!activo);
                                  alternarHora(d.n, hora);
                                }}
                                onMouseEnter={() => pintarSobre(d.n, hora)}
                                aria-label={`${d.corto} ${hora}`}
                                aria-pressed={activo}
                                className={`w-full h-7 rounded-md border transition-colors ${
                                  activo
                                    ? 'bg-teal-600 border-teal-600'
                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-teal-400'
                                }`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button variant="outline" onClick={() => copiarDia(1)} className="mt-5 rounded-full">
                Copiar el lunes a toda la semana
              </Button>
            </TarjetaCuerpo>
          </Tarjeta>

          <Tarjeta>
            <TarjetaTitulo
              titulo="Vacaciones y días bloqueados"
              detalle="Cierra días completos sin tocar tu horario semanal."
            />
            <TarjetaCuerpo>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <Label htmlFor="desde" className="text-xs">Desde</Label>
                  <Input
                    id="desde"
                    type="date"
                    value={bloqueoDesde}
                    onChange={(e) => setBloqueoDesde(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="hasta" className="text-xs">Hasta (opcional)</Label>
                  <Input
                    id="hasta"
                    type="date"
                    value={bloqueoHasta}
                    onChange={(e) => setBloqueoHasta(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="nota" className="text-xs">Motivo (opcional)</Label>
                  <Input
                    id="nota"
                    placeholder="Vacaciones"
                    value={bloqueoNota}
                    onChange={(e) => setBloqueoNota(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <Button variant="outline" onClick={bloquearDias} className="rounded-full">
                <Plane className="w-4 h-4 mr-2" />
                Bloquear estas fechas
              </Button>

              {excepciones.length > 0 && (
                <div className="mt-5 space-y-1.5">
                  {excepciones.map((ex) => (
                    <div
                      key={ex.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-sm"
                    >
                      <span>
                        {format(parseISO(`${ex.date}T00:00:00`), "d 'de' MMMM", { locale: es })}
                        {ex.time && ` · ${ex.time}`}
                        {ex.kind === 'open' && ' (hora extra)'}
                        {ex.note && <span className="text-slate-400"> — {ex.note}</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => quitarExcepcion(ex.id)}
                        className="text-slate-400 hover:text-red-500 p-1"
                        aria-label="Eliminar bloqueo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </TarjetaCuerpo>
          </Tarjeta>
        </div>
      )}

      {/* ---------------- RESERVAS ---------------- */}
      {seccion === 'reservas' && (
        <Tarjeta>
          <TarjetaTitulo titulo="Todas tus reservas" detalle={`${reservas.length} en total`} />
          <TarjetaCuerpo>
            {reservas.length === 0 ? (
              <Vacio icono={CalendarCheck} titulo="Aún no tienes reservas" />
            ) : (
              <div className="space-y-2">
                {reservas.map((r) => (
                  <FilaReserva key={r.id} reserva={r} onAsistencia={marcarAsistencia} />
                ))}
              </div>
            )}
          </TarjetaCuerpo>
        </Tarjeta>
      )}

      {/* ---------------- PACIENTES ---------------- */}
      {seccion === 'pacientes' && (
        <Tarjeta>
          <TarjetaTitulo titulo="Personas que atiendes" detalle="Identificadas solo por su seudónimo." />
          <TarjetaCuerpo>
            {pacientes.length === 0 ? (
              <Vacio icono={Users} titulo="Todavía no atiendes a nadie" />
            ) : (
              <div className="space-y-2">
                {pacientes.map((p) => (
                  <div
                    key={p.key}
                    className="flex flex-wrap items-center justify-between gap-3 py-3 px-4 rounded-lg border border-slate-200 dark:border-slate-800"
                  >
                    <div className="min-w-0">
                      <p className="font-medium flex items-center gap-2 flex-wrap">
                        {p.pseudonym}
                        {p.is_recurring && <Estado tono="exito">Recurrente</Estado>}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {p.completed} {p.completed === 1 ? 'sesión' : 'sesiones'}
                        {p.no_shows > 0 && ` · ${p.no_shows} ausencia(s)`}
                      </p>
                    </div>
                    <p className="text-sm text-right">
                      {p.next_session ? (
                        <span className="text-teal-700 dark:text-teal-400">
                          Próxima: {format(parseISO(p.next_session), 'd MMM, HH:mm', { locale: es })}
                        </span>
                      ) : p.last_session ? (
                        <span className="text-slate-400">
                          Última: {format(parseISO(p.last_session), 'd MMM yyyy', { locale: es })}
                        </span>
                      ) : null}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-sm text-slate-600 dark:text-slate-400">
              <Shield className="w-4 h-4 text-teal-600 flex-none mt-0.5" />
              <span>
                Esta plataforma no guarda notas clínicas ni antecedentes. Ese registro es tuyo
                y debe vivir donde corresponda.
              </span>
            </div>
          </TarjetaCuerpo>
        </Tarjeta>
      )}

      {/* ---------------- INGRESOS ---------------- */}
      {seccion === 'ingresos' && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label htmlFor="mes" className="text-xs">Mes</Label>
              <select
                id="mes"
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
                className="mt-1 h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm first-letter:uppercase"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {format(new Date(2024, m - 1, 1), 'MMMM', { locale: es })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="anio" className="text-xs">Año</Label>
              <Input
                id="anio"
                type="number"
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                className="w-28 mt-1"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Metrica etiqueta="Sesiones pagadas" valor={ingresos?.sessions_count ?? 0} icono={CheckCircle} />
            <Metrica etiqueta="Bruto" valor={pesos(ingresos?.gross ?? 0)} icono={Wallet} />
            <Metrica
              etiqueta={`Neto (menos ${ingresos?.commission_percent ?? 0}%)`}
              valor={pesos(ingresos?.net ?? 0)}
              icono={TrendingUp}
              tono="positivo"
            />
          </div>

          <Tarjeta>
            <TarjetaTitulo
              titulo="Detalle del mes"
              detalle={`${ingresos?.cancellations ?? 0} cancelaciones · ${ingresos?.no_shows ?? 0} ausencias`}
            />
            <TarjetaCuerpo>
              {(ingresos?.sessions?.length ?? 0) === 0 ? (
                <Vacio icono={Wallet} titulo="Sin sesiones pagadas este mes" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                        <th className="pb-2 font-medium">Fecha</th>
                        <th className="pb-2 font-medium">Hora</th>
                        <th className="pb-2 font-medium">Paciente</th>
                        <th className="pb-2 font-medium text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingresos.sessions.map((s) => (
                        <tr key={s.booking_id} className="border-b border-slate-100 dark:border-slate-800/60">
                          <td className="py-2">
                            {format(parseISO(`${s.date}T00:00:00`), 'd MMM', { locale: es })}
                          </td>
                          <td className="py-2 font-mono text-xs">{s.time}</td>
                          <td className="py-2">{s.patient}</td>
                          <td className="py-2 text-right font-medium tabular-nums">{pesos(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TarjetaCuerpo>
          </Tarjeta>

          {valoraciones.length > 0 && (
            <Tarjeta>
              <TarjetaTitulo
                titulo="Lo que dicen de ti"
                detalle="Anónimo: no sabes de quién viene cada comentario."
              />
              <TarjetaCuerpo className="space-y-3">
                {valoraciones.map((v, i) => (
                  <div
                    key={i}
                    className="pb-3 border-b border-slate-100 dark:border-slate-800/60 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center gap-1 mb-1">
                      {Array.from({ length: 5 }, (_, n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${
                            n < v.score ? 'fill-amber-400 text-amber-400' : 'text-slate-200 dark:text-slate-700'
                          }`}
                        />
                      ))}
                      <span className="text-xs text-slate-400 ml-2">{v.month}</span>
                    </div>
                    {v.comment && <p className="text-sm text-slate-600 dark:text-slate-300">{v.comment}</p>}
                  </div>
                ))}
              </TarjetaCuerpo>
            </Tarjeta>
          )}
        </div>
      )}

      {/* ---------------- PERFIL ---------------- */}
      {seccion === 'perfil' && <FormularioPerfil perfil={perfil} onGuardar={guardarPerfil} />}
    </Armazon>
  );
};

/** Una reserva con sus acciones. */
const FilaReserva = ({ reserva, onAsistencia, mostrarSala = false }) => {
  const estado = ESTADOS[reserva.status] || ESTADOS.pending;
  const fecha = reserva.scheduled_date ? parseISO(reserva.scheduled_date) : null;
  const yaPaso = fecha ? fecha < new Date() : false;
  const porMarcar = yaPaso && ['pending', 'confirmed'].includes(reserva.status);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3 px-4 rounded-lg border border-slate-200 dark:border-slate-800">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-medium">{reserva.patient_pseudonym}</span>
          <Estado tono={estado.tono}>{estado.texto}</Estado>
          {reserva.payment_status === 'pending' && reserva.status !== 'cancelled' && (
            <Estado tono="alerta">Sin pagar</Estado>
          )}
        </div>
        {fecha && (
          <p className="text-sm text-slate-500 dark:text-slate-400 first-letter:uppercase">
            {format(fecha, "EEEE d 'de' MMMM, HH:mm", { locale: es })} hrs · {pesos(reserva.amount)}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {mostrarSala && reserva.room_is_open && (
          <a href={reserva.session_url} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              <Video className="w-4 h-4 mr-1.5" />
              Entrar
            </Button>
          </a>
        )}

        {porMarcar && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAsistencia(reserva.id, 'completed')}
              className="rounded-full"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Se realizó
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onAsistencia(reserva.id, 'no_show')}
              className="rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              <XCircle className="w-4 h-4 mr-1" />
              No asistió
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

/** Perfil público del profesional. */
const FormularioPerfil = ({ perfil, onGuardar }) => {
  const [bio, setBio] = useState(perfil?.bio || '');
  const [precio, setPrecio] = useState(perfil?.price_from || 0);
  const [duracion, setDuracion] = useState(perfil?.session_duration_minutes || 50);
  const [especialidades, setEspecialidades] = useState((perfil?.specialties || []).join(', '));
  const [idiomas, setIdiomas] = useState((perfil?.languages || []).join(', '));
  const [foto, setFoto] = useState(perfil?.profile_image || '');
  const [disponible, setDisponible] = useState(perfil?.available ?? true);

  const guardar = () =>
    onGuardar({
      bio,
      price_from: Number(precio),
      session_duration_minutes: Number(duracion),
      specialties: especialidades.split(',').map((s) => s.trim()).filter(Boolean),
      languages: idiomas.split(',').map((s) => s.trim()).filter(Boolean),
      profile_image: foto,
      available: disponible,
    });

  return (
    <Tarjeta>
      <TarjetaTitulo
        titulo="Tu perfil público"
        detalle="Esto es lo que ven las personas al elegir profesional."
      />
      <TarjetaCuerpo className="space-y-5">
        <div className="flex items-center justify-between gap-4 py-3 px-4 rounded-lg bg-slate-50 dark:bg-slate-800/60">
          <div>
            <p className="font-medium">Recibir nuevas reservas</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Si lo apagas, dejas de aparecer en el listado.
            </p>
          </div>
          <Switch checked={disponible} onCheckedChange={setDisponible} />
        </div>

        <div>
          <Label htmlFor="bio">Presentación</Label>
          <Textarea id="bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} className="mt-1" />
          <p className="text-xs text-slate-400 mt-1">
            {bio.length} caracteres · lo ideal son 150 o más
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="precio">Valor por sesión (CLP)</Label>
            <Input
              id="precio"
              type="number"
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="duracion">Duración (minutos)</Label>
            <Input
              id="duracion"
              type="number"
              value={duracion}
              onChange={(e) => setDuracion(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="esp">Especialidades (separadas por coma)</Label>
          <Input id="esp" value={especialidades} onChange={(e) => setEspecialidades(e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label htmlFor="idi">Idiomas (separados por coma)</Label>
          <Input id="idi" value={idiomas} onChange={(e) => setIdiomas(e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label htmlFor="foto">Enlace a tu foto</Label>
          <Input
            id="foto"
            value={foto}
            onChange={(e) => setFoto(e.target.value)}
            placeholder="https://…"
            className="mt-1"
          />
        </div>

        <Button onClick={guardar} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
          <Save className="w-4 h-4 mr-2" />
          Guardar cambios
        </Button>
      </TarjetaCuerpo>
    </Tarjeta>
  );
};

export default PsychologistDashboard;
